/**
 * Toast Adapter
 *
 * Adapter to convert utils ToastShape → component ToastOptions.
 * This file sits in the component layer and bridges the gap between
 * utility-layer types (ToastShape) and component-layer types (ToastOptions).
 *
 * **Why this adapter exists**:
 * - Utils layer should not depend on component types (avoids circular dependencies)
 * - ToastShape is a plain object in utils layer
 * - ToastOptions is component-specific with additional UI properties
 * - Adapter pattern allows clean separation of concerns
 *
 * @module toastAdapter
 */

import React from 'react';
import type { ToastOptions } from '@enfomentis/ui';
import { Button, Link } from '@enfomentis/ui';
import type { ToastShape, ToastActionType } from '@/utils/syncErrorToast';

/**
 * Maps ToastActionType to React endContent element.
 *
 * This function handles the component-layer mapping of action types
 * to actual React buttons/links.
 *
 * @param actionType - The action type from ToastShape
 * @returns React element for toast endContent, or undefined if no action
 */
function mapActionTypeToEndContent(actionType: ToastActionType | undefined): React.ReactNode | undefined {
  if (!actionType) {
    return undefined;
  }

  switch (actionType) {
    case 'refresh':
      return React.createElement(
        Button,
        {
          size: 'sm',
          variant: 'flat',
          color: 'warning',
          'aria-label': 'Refresh page',
          onPress: () => window.location.reload(),
        },
        'Refresh'
      );

    case 'settings-link':
      // Phase 4B2: Link to settings page
      return React.createElement(
        Link,
        {
          href: '/settings/data-sync',
          size: 'sm',
          'aria-label': 'Go to Data & Sync settings',
        },
        'Go to Settings'
      );

    default:
      return undefined;
  }
}

/**
 * Converts ToastShape (utils layer) to ToastOptions (component layer).
 *
 * **Mapping**:
 * - `shape.title` → `options.title`
 * - `shape.description` → `options.description`
 * - `shape.color` → `options.color`
 * - `shape.timeout` → `options.timeout`
 * - `shape.actionType` → `options.endContent` (mapped via mapActionTypeToEndContent)
 *
 * All other ToastOptions fields use component defaults.
 *
 * @param shape - ToastShape from utils layer
 * @returns ToastOptions ready for addToast() or useToastNotification()
 *
 * @example
 * ```typescript
 * import { buildSyncErrorToastShape } from '@/utils/syncErrorToast';
 * import { toastShapeToOptions } from '@/components/shared/Toast';
 * import { addToast } from '@enfomentis/ui';
 *
 * // In error handler
 * const error: SyncError = { code: 'NOT_CONNECTED', ... };
 * const toastShape = buildSyncErrorToastShape(error);
 * const toastOptions = toastShapeToOptions(toastShape);
 * addToast(toastOptions);
 * ```
 */
export function toastShapeToOptions(shape: ToastShape): ToastOptions {
  const options: ToastOptions = {
    title: shape.title,
    description: shape.description,
    color: shape.color,
    // Let component use its defaults for variant, radius, hideIcon, etc.
  };

  // Only include timeout if it's defined (exactOptionalPropertyTypes: true)
  if (shape.timeout !== undefined) {
    options.timeout = shape.timeout;
  }

  // Map actionType to endContent (only if actionType is present)
  const endContent = mapActionTypeToEndContent(shape.actionType);
  if (endContent !== undefined) {
    options.endContent = endContent;
  }

  return options;
}
